import { useCallback } from "react";
import { trackEvents } from "@/common/events/events";
import useIsMobile from "@hooks/useIsMobile";
import { URL_SELL_TRADE_LANDING_v2 } from "@/pages/sell-trade-v2/sell-trade/constants";
import { CARS_PRICES_BASE_PATH } from "@/pages/car-prices/helpers/carsPricesHelpers";
import { CHECKOUT_FUNNEL_PREFIXES, matchesPrefix, normalizeNavPath, USER_AREA_PREFIXES } from "./DesktopHeader/useDesktopNavVariant";
import { CAR_DETAILS_PREFIXES, LISTING_PREFIXES } from "./MobileHeader/useMobileHeaderVariant";

/** Which navigation component the click came from. */

/** Which grouping the clicked item belongs to; `none` for header chrome. */

/**
 * The page the user was on at click time — the six spec values plus `other`.
 *
 * `more` is deliberate, not laziness: it covers the signed-in application
 * pages (account, cart, order tracking, the checkout funnel), which are all
 * "not a public browsing surface" as far as this event is concerned. `other`
 * is the fallback for a path the map below does not know.
 */

/**
 * Sell-your-car journey: the landing page, the form, and its outcome screens.
 */
var SELL_YOUR_CAR_PREFIXES = [URL_SELL_TRADE_LANDING_v2, "/site/sell-my-car-landing", "/site/sell-my-car", "/site/sell-trade-form", "/site/sell-trade-terms", "/site/price-offer", "/schedule"];

/**
 * Public marketing / SEO surfaces: everything indexable that is not the home
 * page, a listing, or a car detail page. `/carsguide` is same-origin editorial
 * content served outside this app, so a webview landing there still reports a
 * real page rather than `other`.
 */
var SEO_LANDING_PREFIXES = ["/landing", "/lp", "/trend", "/city", CARS_PRICES_BASE_PATH, "/financing", "/تقسيط-سيارات", "/products", "/fleet", "/car-showrooms", "/service-centers", "/about-us", "/contact-us", "/faq", "/feedback", "/network", "/تسويق_بالعمولة", "/reviews", "/carsguide", "/compare", "/offerspage", "/terms", "/terms-landing", "/car-purchase-terms-conditions", "/privacy-policy", "/return-policy", "/car-info"];

/**
 * URI → `page_type`, the single definition both web and app report against.
 *
 * The listing, car-detail, funnel and account prefix lists are imported rather
 * than restated, so `page_type` can never disagree with the header variant a
 * page actually renders.
 *
 * Order matters: the sell journey, the funnel and the account area are tested
 * before the SEO list because `/site/*` appears in several of them.
 */
export var getPageType = function getPageType(pathname) {
  var path = normalizeNavPath(pathname);
  if (path === "/") return "home";
  if (matchesPrefix(path, SELL_YOUR_CAR_PREFIXES)) return "sell_your_car";
  if (matchesPrefix(path, CAR_DETAILS_PREFIXES)) return "car_details";
  if (matchesPrefix(path, LISTING_PREFIXES)) return "listing";
  if (matchesPrefix(path, CHECKOUT_FUNNEL_PREFIXES)) return "more";
  if (matchesPrefix(path, USER_AREA_PREFIXES)) return "more";
  if (matchesPrefix(path, SEO_LANDING_PREFIXES)) return "seo_landing";
  return "other";
};

/**
 * `item_name` for a row that comes from `desktopNavData`. Both the desktop mega
 * menu and the responsive drawer render the same objects, so deriving the name
 * from the shared `id` guarantees the two platforms report the same string —
 * `nav-my-info` → `my_info`.
 */
export var navItemName = function navItemName(id) {
  return (id !== null && id !== void 0 ? id : "unknown").replace(/^(mobile-menu|mobile-nav|nav)-/, "").replace(/-/g, "_");
};
export var trackNavigationItemClicked = function trackNavigationItemClicked(_ref) {
  var itemName = _ref.itemName,
    navigationType = _ref.navigationType,
    _ref$navigationGroup = _ref.navigationGroup,
    navigationGroup = _ref$navigationGroup === void 0 ? "none" : _ref$navigationGroup,
    isMobile = _ref.isMobile;
  trackEvents("navigation_item_clicked", {
    // `app` means the site running inside the native webview; the native app's
    // own navigation reports `device_type: mobile`, which web never emits.
    device_type: isMobile ? "responsive" : "desktop",
    navigation_type: navigationType,
    navigation_group: navigationGroup,
    item_name: itemName,
    // Read at click time from the live URL rather than plumbed through props:
    // clicks only ever happen client-side, so this is always the page the user
    // was actually on.
    page_type: getPageType(typeof window === "undefined" ? "/" : window.location.pathname)
  });
};

/**
 * Returns a `track(itemName, group?)` bound to one navigation component.
 * `useIsMobile` has to be read during render, hence the hook.
 */
var useNavItemTracker = function useNavItemTracker(navigationType) {
  var isMobile = useIsMobile();
  return useCallback(function (itemName) {
    var navigationGroup = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "none";
    trackNavigationItemClicked({
      itemName: itemName,
      navigationType: navigationType,
      navigationGroup: navigationGroup,
      isMobile: isMobile
    });
  }, [isMobile, navigationType]);
};
export default useNavItemTracker;